import { useEffect } from 'react';

import { AVAILABLE_OPTION } from 'core/domains/catalog.d';
import { AvailableShowcases } from 'core/services/bff';
import { actions } from 'core/use-cases/showcases';
import { useUserSelector } from 'core/use-cases/user/selectors';
import { useQueryState } from 'core/use-cases/utilities';
import { getLocationPathname } from 'core/utilities';
import { useDispatch } from 'react-redux';

import { useShowcasesAvailableSelector, useShowcasesSelector } from '../selectors';

import type { QueryOptions } from 'core/domains/catalog';
import type { AppDispatch } from 'core/store';

function fetchAllShowcases(dispatch: AppDispatch, availableShowcases: AvailableShowcases[]) {
  const getShowcase = (queryOptions: QueryOptions & { name: string; description: string }) =>
    dispatch(actions.showcasesFetch.request(queryOptions));

  for (const AvailableShowcase of availableShowcases) {
    getShowcase({
      tag: encodeURI(AvailableShowcase.tag),
      itemsPerPage: 12,
      name: AvailableShowcase.name,
      description: AvailableShowcase.description,
      availability: AVAILABLE_OPTION.available,
    });
  }
}

export function useShowcases() {
  const dispatch = useDispatch();
  const pathname = getLocationPathname();

  const { data, isError, isIdle, isLoading, isSuccess } = useQueryState(useShowcasesSelector);

  useEffect(() => {
    if (data.allShowcasesAreFetched) {
      return;
    }

    const canFetchAllShowcases =
      data.availableShowcases.length &&
      !data.allFetchedShowcases.length &&
      pathname === '/departamentos';

    if (canFetchAllShowcases && !isError) {
      fetchAllShowcases(dispatch, data.availableShowcases);
    }
  }, [data.availableShowcases]);

  return {
    data,
    isError,
    isIdle,
    isLoading,
    isSuccess,
  };
}

export const useShowcasesAvailable = () => {
  const dispatch = useDispatch();
  const { data, isError, isIdle, isLoading, isSuccess } = useQueryState(
    useShowcasesAvailableSelector,
  );
  const { isSuccess: successShowCase } = useQueryState(useShowcasesSelector);

  const { data: userData } = useQueryState(useUserSelector);

  const allShowcasesAreFetched = !data.find(({ isFetched }) => !isFetched);
  const cycle = userData?.cycle?.currentCycle;
  const pathname = getLocationPathname();

  const queryOptions = {
    ...(cycle && { cycle }),
    isMultiBrand: userData?.isMultiBrand || false,
  } as QueryOptions;

  const getAvailableShowcases = () =>
    dispatch(actions.availableShowcasesFetch.request(queryOptions));

  useEffect(() => {
    if (queryOptions.cycle && (pathname === '/departamentos' || !data.length) && !successShowCase) {
      getAvailableShowcases();
    }
  }, [queryOptions.cycle]);

  return {
    data,
    isError,
    isIdle,
    isLoading,
    isSuccess,
    allShowcasesAreFetched,
  };
};
